// Apolo IDE — TeamSettingsPopover (sprint subagents-team, E5-WS1-T01).
//
// Engrenagem ao lado do toggle Equipe (só visível com o modo ATIVO): cap de
// operários (2..8, default 4) e modelo dos operários (default: o mesmo da
// sessão — NUNCA hardcodamos modelo; a lista vem do useComposerModels que o
// composer já tem). Prefs persistem em apolo:code:ui (C4) via setPref do
// caller. Popover house-style: painel absoluto, fecha em Escape/fora.

import { useEffect, useRef, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { TEAM_LIMITS } from '../../../hermes/domain/teamPlan.js';

const CAPS = Array.from(
  { length: TEAM_LIMITS.capMax - TEAM_LIMITS.capMin + 1 },
  (_, i) => TEAM_LIMITS.capMin + i,
);

export default function TeamSettingsPopover({ cap, workerModel, readOnly = false, groups = [], onCapChange, onWorkerModelChange, onReadOnlyChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const effectiveCap = CAPS.includes(Number(cap)) ? Number(cap) : TEAM_LIMITS.capDefault;

  return (
    <div ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open ? 'true' : 'false'}
        aria-haspopup="dialog"
        aria-label="Configurações da equipe"
        title="Configurações da equipe (operários, modelo)"
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-transparent bg-apolo-surface2/40 text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
      >
        <Settings2 size={11} aria-hidden="true" />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Configurações da equipe"
          className="absolute bottom-full left-0 z-30 mb-2 w-64 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1 p-3 shadow-lg"
        >
          <label className="block text-[11px] text-apolo-textTertiary">
            Operários (máx. por equipe)
            <select
              value={effectiveCap}
              onChange={(e) => onCapChange?.(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-xs text-apolo-textPrimary"
            >
              {CAPS.map((n) => (
                <option key={n} value={n}>
                  {n}{n === TEAM_LIMITS.capDefault ? ' (padrão)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="mt-3 block text-[11px] text-apolo-textTertiary">
            Modelo dos operários
            <select
              value={workerModel || ''}
              onChange={(e) => onWorkerModelChange?.(e.target.value || null)}
              className="mt-1 w-full rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-xs text-apolo-textPrimary"
            >
              <option value="">Mesmo da sessão (padrão)</option>
              {groups.map((g) => (
                <optgroup key={g.provider} label={g.provider}>
                  {(g.models || []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          <label className="mt-3 flex items-start gap-2 text-[11px] text-apolo-textTertiary">
            <input
              type="checkbox"
              checked={!!readOnly}
              onChange={(e) => onReadOnlyChange?.(e.target.checked)}
              className="mt-0.5 accent-apolo-accent"
            />
            <span>
              <span className="text-apolo-textSecondary">Modo análise (read-only)</span>
              <br />
              Operários só leem e relatam — escrita e comandos bloqueados.
            </span>
          </label>
        </div>
      ) : null}
    </div>
  );
}
